/** @jsx jsx */

import { ComponentProps, Fragment } from 'react';
import { css, jsx } from '@emotion/core';

import { AppStatusName } from 'app/developer-v3/types/statuses';
import { isLastDescendantNodeClicked } from 'app/developer-v3/utils';
import {
  CurrentVersionReadOnlyReason,
  useActiveProfile,
  useCurrentVersionReadOnlyReason,
} from 'app/developer-v3/hooks';

import ColumnActions from './ColumnActions';
import ColumnActivity from './ColumnActivity';
import ColumnChangelog from './ColumnChangelog';
import ColumnHistory from './ColumnHistory';
import ColumnLastUpdated from './ColumnLastUpdated';
import ColumnStatus from './ColumnStatus';
import { getVersionStatusLabel } from './utils';

import type { SemVer } from 'app/developer-v3/types/version';
import type { Version } from './types';

const Styles = {
  cell: ({
    hideBottomBorder,
    noPadding,
    topRight,
  }: {
    hideBottomBorder?: boolean;
    noPadding?: boolean;
    topRight?: boolean;
  }) => [
    css`
      :first-of-type {
        padding-left: 0.5em;
      }
      :last-of-type {
        padding-right: 0.5em;
      }
    `,
    hideBottomBorder &&
      css`
        border-bottom-style: none !important;
      `,
    noPadding &&
      css`
        padding: 0px !important;
      `,
    topRight &&
      css`
        text-align: right;
        vertical-align: top;
      `,
  ],
  row: css`
    cursor: pointer;
  `,
};

type Props = {
  integrationId: number;
  integrationStatus: AppStatusName;
  isVisibleChangelog: boolean;
  isVisibleAuditLogs: boolean;
  getMenuItems: (
    version: Version
  ) => ComponentProps<typeof ColumnActions>['items'];
  version: Version;
  viewAppVersion: (appVersion: SemVer) => void;
};

export default function RowVersion({
  integrationId,
  integrationStatus,
  isVisibleChangelog,
  isVisibleAuditLogs,
  getMenuItems,
  version,
  viewAppVersion,
}: Props) {
  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const isNotAdmin =
    currentVersionReadOnlyReason === CurrentVersionReadOnlyReason.NOT_ADMIN;

  const {
    activeProfile,
    isLoading: isLoadingActiveProfile,
  } = useActiveProfile();
  const isStaff = activeProfile?.isStaff ?? false;

  const versionStatusLabel = getVersionStatusLabel(version.lifecycle.status);

  const onClickPrimaryRow = event => {
    // We added emails in the last-updated column and we don't want to take the
    // user to view the app version in case they click on an email.
    if (!isLastDescendantNodeClicked(event) && event.target?.tagName !== 'A') {
      viewAppVersion(version.version);
    }
  };

  const onClickChangelogRow = event => {
    // The changelog column contains markdowns that could have emails and links.
    // So, we don't want to take the user to view the app version in case they
    // click on an anchor.
    if (event.target?.tagName !== 'A') {
      viewAppVersion(version.version);
    }
  };

  const onClickAuditLogsRow = () => {
    viewAppVersion(version.version);
  };

  const hasChangelog = !!version.changelog;
  // Each version should come with audit logs always. So when the UI wants to
  // show the audit logs, we assume that there are audit logs to display.
  const hasAuditLogs = isVisibleAuditLogs;

  return (
    <Fragment key={version.version}>
      <tr
        aria-label="Click to view this version"
        css={Styles.row}
        onClick={event => onClickPrimaryRow(event)}
      >
        <td
          css={Styles.cell({
            hideBottomBorder:
              (isVisibleChangelog && hasChangelog) ||
              (isVisibleAuditLogs && hasAuditLogs),
          })}
        >
          {version.version}
        </td>
        <td css={Styles.cell({ hideBottomBorder: false })}>
          <ColumnStatus
            status={integrationStatus}
            version={version}
            versionStatusLabel={versionStatusLabel}
          />
        </td>
        <td css={Styles.cell({ hideBottomBorder: false })}>
          <ColumnActivity integrationId={integrationId} version={version} />
        </td>
        <td css={Styles.cell({ hideBottomBorder: false })}>
          <ColumnLastUpdated version={version} />
        </td>
        <td css={Styles.cell({ hideBottomBorder: false })}>
          {version.platformVersion}
        </td>
        <td css={Styles.cell({ hideBottomBorder: false })}>
          <ColumnActions
            isNotAdmin={isNotAdmin}
            items={getMenuItems(version)}
          />
        </td>
      </tr>
      {isVisibleChangelog && hasChangelog && (
        <tr
          aria-label="Click to view this version"
          css={Styles.row}
          onClick={event => onClickChangelogRow(event)}
        >
          <td
            css={Styles.cell({
              hideBottomBorder: isVisibleAuditLogs && hasAuditLogs,
              topRight: true,
            })}
          />
          <td colSpan={5} css={Styles.cell({ hideBottomBorder: false })}>
            <ColumnChangelog version={version} />
          </td>
        </tr>
      )}
      {isVisibleAuditLogs && hasAuditLogs && (
        <tr
          aria-label="Click to view this version"
          css={Styles.row}
          onClick={onClickAuditLogsRow}
        >
          <td css={Styles.cell({ topRight: true })} />
          <td
            colSpan={5}
            css={Styles.cell({
              hideBottomBorder: isVisibleAuditLogs && hasAuditLogs,
              noPadding: true,
            })}
          >
            <ColumnHistory
              integrationId={integrationId}
              isLoadingActiveProfile={isLoadingActiveProfile}
              isStaff={isStaff}
              version={version.version}
            />
          </td>
        </tr>
      )}
    </Fragment>
  );
}
